import { useEffect, useRef, useState } from 'react';

import { convertSiteCmsHtmlApi } from '@/lib/api/siteCmsApi';
import { CMS_HTML_LIMITS, cmsHtmlDocCharacters, formatCmsHtmlLimit, normalizeCmsHtmlDoc } from '@shared/cms/html-doc';
import type {
  SiteCmsCompetitionCard,
  SiteCmsDocumentResponse,
  SiteCmsHtmlConvertResult,
  SiteCmsHtmlNode,
  SiteCmsOwnerType,
  SiteCmsPageDraft,
} from '@shared/types/entities';
import Placeholder from '@tiptap/extension-placeholder';
import { type Editor, EditorContent, Node, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { toast } from 'sonner';

import { renderHtmlDoc } from '../../../../../packages/site-renderer/src/html-doc.mjs';
import { escapeHtml } from '../../../../../packages/site-renderer/src/render-core.mjs';
import publicStyles from '../../../../../packages/site-renderer/static/site.css?raw';
import { SiteCmsDialog } from './SiteCmsDialog';
import SiteCmsLinkPicker from './SiteCmsLinkPicker';
import {
  type CmsHtmlClipboard,
  cmsHtmlFromClipboard,
  cmsHtmlNewTable,
  cmsHtmlTableEdit,
  createCmsPasteGuard,
} from './site-cms-html-model';
import { cmsIsSafeLink } from './site-cms-interaction-model';

/**
 * Minimal table schema matching the stored contract exactly (no colspan,
 * rowspan or widths exist). Pasted HTML is never parsed by Tiptap: every
 * paste goes through the API converter (see handlePaste).
 */
const TABLE_NODES = [
  Node.create({
    name: 'table',
    group: 'block',
    content: 'tableRow+',
    isolating: true,
    parseHTML: () => [{ tag: 'table' }],
    renderHTML: () => ['div', { class: 'cms-html-editor-table' }, ['table', ['tbody', 0]]],
  }),
  Node.create({
    name: 'tableRow',
    content: '(tableHeader | tableCell)+',
    parseHTML: () => [{ tag: 'tr' }],
    renderHTML: () => ['tr', 0],
  }),
  Node.create({
    name: 'tableHeader',
    content: 'paragraph+',
    isolating: true,
    parseHTML: () => [{ tag: 'th' }],
    renderHTML: () => ['th', 0],
  }),
  Node.create({
    name: 'tableCell',
    content: 'paragraph+',
    isolating: true,
    parseHTML: () => [{ tag: 'td' }],
    renderHTML: () => ['td', 0],
  }),
];

type Notice = { kind: 'ok' | 'error'; title: string; items?: string[] };
type Review = {
  token: { serial: number; version: number };
  from: number;
  to: number;
  result: SiteCmsHtmlConvertResult;
};
type Props = {
  ownerType: SiteCmsOwnerType;
  ownerId: number;
  doc: SiteCmsHtmlNode;
  onChange: (doc: SiteCmsHtmlNode) => void;
  resources: {
    pages: SiteCmsPageDraft[];
    documents: SiteCmsDocumentResponse[];
    competitions: SiteCmsCompetitionCard[];
  };
};

function ancestors(editor: Editor): Array<{ name: string; depth: number }> {
  const { $from } = editor.state.selection;
  return Array.from({ length: $from.depth }, (_, i) => ({ name: $from.node(i + 1).type.name, depth: i + 1 }));
}
function tableContext(editor: Editor) {
  const { $from } = editor.state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === 'table')
      return {
        pos: $from.before(depth),
        node,
        row: $from.index(depth),
        column: depth + 1 <= $from.depth ? $from.index(depth + 1) : 0,
      };
  }
  return null;
}
/** Inert, script-free rendering of the shared publisher output. */
function previewSource(doc: SiteCmsHtmlNode): string {
  return `<!doctype html><html lang="fr" data-theme="marine"><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'"><style>${publicStyles}</style></head><body><main class="container editorial-wrap"><section class="content-section">${renderHtmlDoc(doc, escapeHtml, () => '#')}</section></main></body></html>`;
}
const TOO_LARGE = 'Contenu collé trop volumineux (256 Ko de HTML au maximum). Importez-le en plusieurs fois.';

export default function SiteCmsHtmlEditor({ ownerType, ownerId, doc, onChange, resources }: Props) {
  const callback = useRef(onChange);
  useEffect(() => {
    callback.current = onChange;
  }, [onChange]);
  const version = useRef(0);
  const [guard] = useState(createCmsPasteGuard);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [codeOpen, setCodeOpen] = useState(false);
  const [code, setCode] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [href, setHref] = useState('https://');
  // Latest analysis callback for the editor's paste handler (assigned in an effect).
  const analyse = useRef<(payload: CmsHtmlClipboard) => void>(() => undefined);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
        underline: false,
        trailingNode: false,
        link: { openOnClick: false, autolink: false, linkOnPaste: false },
      }),
      ...TABLE_NODES,
      Placeholder.configure({ placeholder: 'Collez ici un contenu copié depuis Word ou une page web…' }),
    ],
    content: doc,
    // The toolbar (table actions, active marks) follows the cursor.
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-label': 'Contenu HTML / Word',
        'aria-multiline': 'true',
        class: 'cms-rich-prose cms-html-prose',
      },
      // ProseMirror must not even build DOM from pasted HTML: the API converts it.
      transformPastedHTML: () => '',
      // Every paste (Word, browser, plain text) is converted by the API.
      handlePaste: (_view, event) => {
        event.preventDefault();
        const payload = cmsHtmlFromClipboard(event.clipboardData);
        if (payload) analyse.current(payload);
        return true;
      },
      // External drops would bypass the converter.
      handleDrop: (_view, _event, _slice, moved) => !moved,
    },
    onTransaction: ({ transaction }) => {
      if (transaction.docChanged) version.current++;
    },
    onUpdate: ({ editor: instance }) => callback.current(normalizeCmsHtmlDoc(instance.getJSON())),
  });

  // External changes (structure undo, reload, page switch) replace the editor content.
  useEffect(() => {
    if (!editor) return;
    if (JSON.stringify(normalizeCmsHtmlDoc(editor.getJSON())) !== JSON.stringify(doc))
      editor.commands.setContent(doc, { emitUpdate: false });
  }, [editor, doc]);
  useEffect(() => () => guard.cancel(), [guard]);

  function insert(result: SiteCmsHtmlConvertResult, from: number, to: number, mode: 'cursor' | 'replace') {
    if (!editor || !result.doc) return;
    const content = result.doc.content ?? [];
    const range = mode === 'replace' ? { from: 0, to: editor.state.doc.content.size } : { from, to };
    if (mode === 'cursor') {
      const names = new Set(ancestors(editor).map(a => a.name));
      if (names.has('table') && content.some(node => node.type !== 'paragraph')) {
        setNotice({
          kind: 'error',
          title: 'Placez le curseur hors du tableau pour coller ce contenu : rien n’a été modifié.',
        });
        return;
      }
      if (names.has('listItem') && content.some(node => node.type === 'table')) {
        setNotice({
          kind: 'error',
          title: 'Placez le curseur hors de la liste pour coller un tableau : rien n’a été modifié.',
        });
        return;
      }
    }
    editor.chain().focus().insertContentAt(range, content).run();
    const stats = result.stats;
    setNotice({
      kind: 'ok',
      title: `Contenu ${mode === 'replace' ? 'remplacé' : 'inséré'} et nettoyé${stats ? ` : ${stats.characters} caractères${stats.tables ? `, ${stats.tables} tableau(x)` : ''}` : ''}.`,
      items: result.warnings.map(w => w.message),
    });
    toast.success('Contenu importé. Vous pouvez annuler.', {
      action: { label: 'Annuler', onClick: () => editor.chain().focus().undo().run() },
    });
  }

  async function runAnalysis(payload: CmsHtmlClipboard, fromDialog = false) {
    if (!editor) return;
    if ((payload.html ?? payload.text ?? '').length > CMS_HTML_LIMITS.maxSourceChars) {
      setNotice({ kind: 'error', title: TOO_LARGE });
      return;
    }
    const { from, to } = editor.state.selection;
    const token = guard.begin(version.current);
    setBusy(true);
    setNotice(null);
    try {
      const result = await convertSiteCmsHtmlApi({ ownerType, ownerId, ...payload });
      const decision = guard.accepts(token, version.current);
      if (decision === 'stale') return;
      if (decision === 'changed') {
        setNotice({
          kind: 'error',
          title: 'Le contenu a changé pendant l’analyse : rien n’a été inséré. Collez à nouveau.',
        });
        return;
      }
      if (result.status === 'rejected') {
        setNotice({
          kind: 'error',
          title: 'Import refusé : le contenu de la section n’a pas été modifié.',
          items: result.errors.map(e => e.message),
        });
        return;
      }
      if (result.status === 'warning' || fromDialog) setReview({ token, from, to, result });
      else insert(result, from, to, 'cursor');
    } catch (reason) {
      if (guard.accepts(token, version.current) === 'stale') return;
      setNotice({
        kind: 'error',
        title: `Analyse impossible : ${reason instanceof Error ? reason.message : 'erreur inconnue'}. Le contenu n’a pas été modifié.`,
      });
    } finally {
      if (guard.accepts(token, version.current) !== 'stale') setBusy(false);
    }
  }
  useEffect(() => {
    analyse.current = payload => void runAnalysis(payload);
  });

  function confirm(mode: 'cursor' | 'replace') {
    if (!review) return;
    const decision = guard.accepts(review.token, version.current);
    setReview(null);
    setCodeOpen(false);
    if (decision !== 'apply') {
      setNotice({
        kind: 'error',
        title: 'Le contenu a changé depuis l’analyse : rien n’a été inséré. Recommencez le collage.',
      });
      return;
    }
    setCode('');
    insert(review.result, review.from, review.to, mode);
  }
  function cancelReview() {
    guard.cancel();
    setReview(null);
    setBusy(false);
    setNotice({ kind: 'ok', title: 'Import annulé : le contenu n’a pas été modifié.' });
  }

  if (!editor) return <p className='cms-hint'>Chargement de l’éditeur…</p>;
  const table = tableContext(editor);
  const editTable = (action: 'addRow' | 'removeRow' | 'addColumn' | 'removeColumn') => {
    if (!table) return;
    const next = cmsHtmlTableEdit(normalizeCmsHtmlDoc(table.node.toJSON()), action, table.row, table.column);
    if (next === 'limit') {
      toast.error(
        `Un tableau est limité à ${CMS_HTML_LIMITS.maxRows} lignes et ${CMS_HTML_LIMITS.maxColumns} colonnes.`,
      );
      return;
    }
    const range = { from: table.pos, to: table.pos + table.node.nodeSize };
    if (next === null) editor.chain().focus().deleteRange(range).run();
    else editor.chain().focus().insertContentAt(range, next).run();
  };
  const characters = cmsHtmlDocCharacters(doc);
  const tool = (label: string, run: () => void, options: { active?: boolean; disabled?: boolean } = {}) => (
    <button
      type='button'
      aria-pressed={options.active}
      disabled={options.disabled || busy}
      onMouseDown={event => event.preventDefault()}
      onClick={run}
    >
      {label}
    </button>
  );

  return (
    <div className='cms-rich-input cms-html-input'>
      <div className='cms-rich-toolbar' role='group' aria-label='Mise en forme du contenu HTML / Word'>
        {tool('H2', () => editor.chain().focus().toggleHeading({ level: 2 }).run(), {
          active: editor.isActive('heading', { level: 2 }),
          disabled: Boolean(table),
        })}
        {tool('H3', () => editor.chain().focus().toggleHeading({ level: 3 }).run(), {
          active: editor.isActive('heading', { level: 3 }),
          disabled: Boolean(table),
        })}
        {tool('Gras', () => editor.chain().focus().toggleBold().run(), { active: editor.isActive('bold') })}
        {tool('Italique', () => editor.chain().focus().toggleItalic().run(), { active: editor.isActive('italic') })}
        {tool('Liste', () => editor.chain().focus().toggleBulletList().run(), {
          active: editor.isActive('bulletList'),
          disabled: Boolean(table),
        })}
        {tool('Liste numérotée', () => editor.chain().focus().toggleOrderedList().run(), {
          active: editor.isActive('orderedList'),
          disabled: Boolean(table),
        })}
        {tool('Lien', () => {
          setHref(String(editor.getAttributes('link').href ?? 'https://'));
          setLinkOpen(true);
        })}
        {tool('Retirer le lien', () => editor.chain().focus().unsetLink().run(), {
          disabled: !editor.isActive('link'),
        })}
        {tool('Tableau', () => editor.chain().focus().insertContent(cmsHtmlNewTable()).run(), {
          disabled: Boolean(table) || editor.isActive('listItem'),
        })}
        {table && (
          <>
            {tool('+ Ligne', () => editTable('addRow'))}
            {tool('− Ligne', () => editTable('removeRow'))}
            {tool('+ Colonne', () => editTable('addColumn'))}
            {tool('− Colonne', () => editTable('removeColumn'))}
            {tool('Supprimer le tableau', () =>
              editor
                .chain()
                .focus()
                .deleteRange({ from: table.pos, to: table.pos + table.node.nodeSize })
                .run(),
            )}
          </>
        )}
        {tool('Annuler', () => editor.chain().focus().undo().run(), { disabled: !editor.can().undo() })}
        {tool('Rétablir', () => editor.chain().focus().redo().run(), { disabled: !editor.can().redo() })}
        {tool('Coller du HTML', () => setCodeOpen(true))}
      </div>
      {linkOpen && (
        <div className='cms-row'>
          <SiteCmsLinkPicker value={href} onChange={setHref} {...resources} />
          <button
            type='button'
            className='cms-button'
            onClick={() => {
              if (!cmsIsSafeLink(href)) {
                toast.error('Utilisez une page interne ou une adresse HTTPS.');
                return;
              }
              editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
              setLinkOpen(false);
            }}
          >
            Appliquer
          </button>
          <button type='button' className='cms-link-button' onClick={() => setLinkOpen(false)}>
            Annuler
          </button>
        </div>
      )}
      <EditorContent editor={editor} />
      {busy && (
        <p className='cms-hint' role='status'>
          Analyse et nettoyage du contenu…
        </p>
      )}
      {notice && (
        <div
          className={`cms-html-notice ${notice.kind === 'error' ? 'is-error' : 'is-ok'}`}
          role={notice.kind === 'error' ? 'alert' : 'status'}
        >
          <strong>{notice.title}</strong>
          {notice.items?.length ? (
            <ul>
              {notice.items.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
      <small className={characters > CMS_HTML_LIMITS.maxCharacters ? 'cms-danger' : ''}>
        {characters.toLocaleString('fr-FR')}/{formatCmsHtmlLimit(CMS_HTML_LIMITS.maxCharacters)} caractères · Collez
        depuis Word ou une page web : le contenu est nettoyé (sans styles, images ni scripts).
      </small>
      {codeOpen && !review && (
        <SiteCmsDialog title='Coller du HTML' busy={busy} onClose={() => setCodeOpen(false)}>
          <label>
            Code HTML
            <textarea
              rows={10}
              value={code}
              spellCheck={false}
              onChange={event => setCode(event.target.value)}
              placeholder='<h2>Titre</h2><p>Votre texte…</p>'
            />
          </label>
          <small className={code.length > CMS_HTML_LIMITS.maxSourceChars ? 'cms-danger' : ''}>
            {code.length > CMS_HTML_LIMITS.maxSourceChars
              ? TOO_LARGE
              : 'Le code est analysé puis converti en contenu structuré. Les scripts, styles et images sont retirés ; chaque nettoyage est listé avant insertion.'}
          </small>
          {notice?.kind === 'error' && (
            <div className='cms-html-notice is-error' role='alert'>
              <strong>{notice.title}</strong>
              {notice.items?.length ? (
                <ul>
                  {notice.items.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          )}
          <div className='cms-dialog-actions'>
            <button type='button' className='cms-link-button' disabled={busy} onClick={() => setCodeOpen(false)}>
              Annuler
            </button>
            <button
              type='button'
              className='cms-button'
              disabled={busy || !code.trim() || code.length > CMS_HTML_LIMITS.maxSourceChars}
              onClick={() => void runAnalysis({ source: 'code', html: code }, true)}
            >
              {busy ? 'Analyse…' : 'Analyser'}
            </button>
          </div>
        </SiteCmsDialog>
      )}
      {review && (
        <SiteCmsDialog title='Vérifier le contenu importé' wide onClose={cancelReview}>
          {review.result.warnings.length ? (
            <div className='cms-html-notice is-warning' role='status'>
              <strong>Transformations à confirmer :</strong>
              <ul>
                {review.result.warnings.map(w => (
                  <li key={w.code}>{w.message}</li>
                ))}
              </ul>
            </div>
          ) : (
            <p>Le contenu a été nettoyé sans perte. Vérifiez l’aperçu avant de l’insérer.</p>
          )}
          <iframe
            className='cms-html-review'
            title='Aperçu du contenu importé'
            sandbox=''
            srcDoc={previewSource(review.result.doc!)}
          />
          <div className='cms-dialog-actions'>
            <button type='button' className='cms-link-button' onClick={cancelReview}>
              Annuler
            </button>
            <button type='button' className='cms-button cms-button-soft' onClick={() => confirm('replace')}>
              Remplacer tout le contenu
            </button>
            <button type='button' className='cms-button' onClick={() => confirm('cursor')}>
              Insérer au curseur
            </button>
          </div>
        </SiteCmsDialog>
      )}
    </div>
  );
}
